import { CandidacyRejectAction } from "./CandidacyRejectAction";
import { CandidacyStatusDropdown } from "./CandidacyStatusDropdown";
import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import {
  CandidacyOwnerCell,
  ErrorText,
  Row,
  SheetNavigation,
  Text,
} from "@/components";
import { Skeleton } from "@/components/ui/skeleton";

import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyStatus } from "@/types";

export const CandidateSheetActionsContainer = ({
  canMoveToNextCandidacy,
  canMoveToPreviousCandidacy,
  currentCandidacyIndex,
  handleMoveToNextCandidacy,
  handleMoveToPreviousCandidacy,
  onStatusChanged,
  setOpenStatusChangeDialog,
  totalCandidaciesCount,
}: {
  canMoveToNextCandidacy: boolean;
  canMoveToPreviousCandidacy: boolean;
  currentCandidacyIndex: number;
  handleMoveToNextCandidacy: () => void;
  handleMoveToPreviousCandidacy: () => void;
  onStatusChanged?: (newStatus: CandidacyStatus) => void;
  setOpenStatusChangeDialog: (status: CandidacyStatus) => void;
  totalCandidaciesCount: number;
}) => {
  /* Context */
  const { candidacyId } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const {
    data: candidacyInfo,
    isError: isCandidacyInfoError,
    isPending: isCandidacyInfoPending,
  } = useGetCandidacyInfo({ candidacyId });

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  /* Render */
  const renderNavigation = () => {
    return (
      <SheetNavigation
        betweenButtons={
          // -1 = selected candidacy isn't in the current filtered ordering
          // (?c= deep link or filter change), so a position would be a lie.
          currentCandidacyIndex >= 0 ? (
            <Text className="text-xs font-medium tabular-nums text-slate-600">
              {currentCandidacyIndex + 1} / {totalCandidaciesCount}
            </Text>
          ) : null
        }
        canMoveToNext={canMoveToNextCandidacy}
        canMoveToPrevious={canMoveToPreviousCandidacy}
        handleMoveToNext={handleMoveToNextCandidacy}
        handleMoveToPrevious={handleMoveToPreviousCandidacy}
        screenName={MIXPANEL_SCREENS.CANDIDATE_SHEET}
        variant="ghost"
      />
    );
  };

  const renderOwner = () => {
    if (!candidacyInfo) return null;

    return (
      <CandidacyOwnerCell
        agentClaimScreen={MIXPANEL_SCREENS.CANDIDATE_SHEET}
        candidacyId={candidacyId}
        candidacyStatus={candidacyInfo.status}
        onAgentStarted={() => {
          setCandidacyInAllLists({
            candidacyId,
            stafferAgentActive: true,
          });
          setCandidacyInfo({
            candidacyId,
            stafferAgentActive: true,
          });
        }}
        owners={candidacyInfo.candidacyOwners}
        promptAgentOnClaim
        showTooltip
      />
    );
  };

  const render = () => {
    if (isCandidacyInfoPending) {
      return (
        <Row className="shrink-0 items-center gap-1.5">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-32 rounded-full" />
          {renderNavigation()}
        </Row>
      );
    }
    if (isCandidacyInfoError) {
      return <ErrorText errorMessage="Error retrieving candidacy." />;
    }
    return (
      <Row className="shrink-0 items-center gap-1.5">
        <CandidacyRejectAction candidacyInfo={candidacyInfo} />
        <CandidacyStatusDropdown
          candidacyInfo={candidacyInfo}
          onStatusChanged={onStatusChanged}
          setOpenStatusChangeDialog={setOpenStatusChangeDialog}
        />
        {renderOwner()}
        {renderNavigation()}
      </Row>
    );
  };

  return render();
};
